export const CAPABILITY_ASSET_UPLOAD_MAX_FILES = 250;
export const CAPABILITY_ASSET_UPLOAD_MAX_TOTAL_BYTES = 250 * 1024 * 1024;

const SUPPORTED_ASSET_TYPES = new Set(['video', 'image', 'audio', 'document', 'file']);

export function normalizeCapabilityAssetUploadItems(files) {
  return Array.from(files || []).map((item) => {
    const file = item.file || item;
    return {
      file,
      path: item.path || file.webkitRelativePath || file.name,
      size: item.size ?? file.size ?? 0,
      assetType: item.assetType || item.asset_type,
    };
  });
}

export function cleanCapabilityAssetRelativePath(path) {
  const segments = String(path || '')
    .replace(/\\/g, '/')
    .split('/')
    .map((segment) => segment.trim())
    .filter((segment) => segment && segment !== '.' && segment !== '..')
    .map((segment) => segment.replace(/[^\w .@-]/g, '-'));

  return segments.join('/') || 'upload';
}

function extensionForPath(path) {
  const fileName = cleanCapabilityAssetRelativePath(path).split('/').pop() || '';
  const index = fileName.lastIndexOf('.');
  return index >= 0 ? fileName.slice(index).toLowerCase() : '';
}

export function inferCapabilityAssetType({ path, mimeType, assetType } = {}) {
  const explicit = String(assetType || '').toLowerCase();
  if (SUPPORTED_ASSET_TYPES.has(explicit)) return explicit;

  const mime = String(mimeType || '').toLowerCase();
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime === 'application/pdf' || mime.startsWith('text/')) return 'document';

  const extension = extensionForPath(path);
  if (['.mp4', '.mov', '.webm', '.mkv'].includes(extension)) return 'video';
  if (['.png', '.jpg', '.jpeg', '.gif', '.webp'].includes(extension)) return 'image';
  if (['.mp3', '.wav', '.m4a', '.ogg'].includes(extension)) return 'audio';
  if (['.pdf', '.txt', '.md', '.json', '.csv'].includes(extension)) return 'document';

  return explicit ? 'unsupported' : 'file';
}

const CHAT_CAPABILITY_ASSET_TYPES = new Set(['video', 'image', 'audio']);

export function partitionChatAttachmentItems(entries) {
  const assets = [];
  const documents = [];
  for (const entry of Array.from(entries || [])) {
    const file = entry.file || entry;
    const assetType = inferCapabilityAssetType({
      path: entry.path || file.webkitRelativePath || file.name,
      mimeType: file.type,
      assetType: entry.assetType || entry.asset_type,
    });
    (CHAT_CAPABILITY_ASSET_TYPES.has(assetType) ? assets : documents).push(entry);
  }
  return { assets, documents };
}

export function validateCapabilityAssetUploadItems(
  items,
  {
    maxFiles = CAPABILITY_ASSET_UPLOAD_MAX_FILES,
    maxTotalBytes = CAPABILITY_ASSET_UPLOAD_MAX_TOTAL_BYTES,
  } = {}
) {
  if (!items.length) return 'Select at least one asset.';
  if (items.length > maxFiles) return `Select ${maxFiles} files or fewer.`;

  const totalBytes = items.reduce((sum, item) => sum + (item.size || 0), 0);
  if (totalBytes > maxTotalBytes) {
    return `Total asset upload size must be ${Math.round(maxTotalBytes / 1024 / 1024)}MB or less.`;
  }

  const unsupported = items.find((item) => inferCapabilityAssetType({
    path: item.path,
    mimeType: item.file?.type,
    assetType: item.assetType,
  }) === 'unsupported');
  if (unsupported) return `"${unsupported.path}" is not a supported asset type.`;

  return null;
}

export async function buildCapabilityAssetHandoffPayload(
  items,
  {
    projectId,
    uploadBatchId,
    destination,
    readFileBase64,
  }
) {
  if (!projectId) throw new Error('projectId is required');
  if (typeof readFileBase64 !== 'function') throw new Error('readFileBase64 is required');

  const files = await Promise.all(
    items.map(async (item) => {
      const mimeType = item.file?.type || 'application/octet-stream';
      return {
        path: cleanCapabilityAssetRelativePath(item.path),
        content: await readFileBase64(item.file),
        encoding: 'base64',
        mime_type: mimeType,
        asset_type: inferCapabilityAssetType({
          path: item.path,
          mimeType,
          assetType: item.assetType,
        }),
      };
    })
  );

  const payload = {
    project_id: projectId,
    files,
  };
  if (uploadBatchId) payload.upload_batch_id = uploadBatchId;
  if (destination) payload.destination = destination;
  return payload;
}

export function summarizeCapabilityAssetHandoffResult(result, fallbackCount = 0) {
  const data = result?.data || result || {};
  const savedCount = Array.isArray(data.persisted_assets?.saved)
    ? data.persisted_assets.saved.length
    : fallbackCount;
  const actionCount = Array.isArray(data.effects?.next_actions) ? data.effects.next_actions.length : 0;
  const parts = [`${savedCount} asset${savedCount === 1 ? '' : 's'} uploaded`];
  if (actionCount > 0) parts.push(`${actionCount} MCP action${actionCount === 1 ? '' : 's'} prepared`);
  return `${parts.join('. ')}.`;
}

function capabilityAssetHandoffBody(response) {
  return response?.data ?? response ?? {};
}

function isCapabilityAssetRuntimePath(value) {
  const path = String(value || '');
  if (!path.startsWith('/workspace/capability-assets/') || path.includes('\\')) return false;
  return path.split('/').slice(1).every((segment) => segment && segment !== '.' && segment !== '..');
}

export function capabilityAssetFileRefsFromHandoff(response) {
  const body = capabilityAssetHandoffBody(response);
  const handoff = body.handoff && typeof body.handoff === 'object' ? body.handoff : {};
  const manifest = handoff.asset_manifest && typeof handoff.asset_manifest === 'object'
    ? handoff.asset_manifest
    : {};
  return Array.from(handoff.assets || []).flatMap((asset) => {
    if (!asset || asset.resource_type !== 'source_media_asset') return [];
    const runtimePath = String(asset.runtime_path || '').trim();
    if (!isCapabilityAssetRuntimePath(runtimePath)) return [];
    return [{
      type: 'file_ref',
      file_name: String(asset.source_path || runtimePath.split('/').pop() || asset.id || 'asset'),
      file_path: runtimePath,
      file_mime_type: String(asset.mime_type || 'application/octet-stream'),
      file_size_bytes: Number.isFinite(Number(asset.byte_size)) ? Number(asset.byte_size) : null,
      metadata: {
        schema_version: 'bluebear.capability_asset_ref.v1',
        resource_type: 'source_media_asset',
        source_media_asset_id: String(asset.id || ''),
        asset_manifest_id: String(manifest.id || ''),
        project_id: String(asset.project_id || manifest.project_id || ''),
        upload_batch_id: String(asset.upload_batch_id || manifest.upload_batch_id || ''),
        asset_type: String(asset.asset_type || 'file'),
        content_hash: String(asset.content_hash || ''),
        workspace_id: String(asset.workspace_id || body.workspace_id || ''),
      },
    }];
  });
}

export function capabilityAssetRuntimeContext(fileRefs) {
  const assets = Array.from(fileRefs || []).flatMap((ref) => {
    const metadata = ref?.metadata || {};
    if (metadata.schema_version !== 'bluebear.capability_asset_ref.v1') return [];
    if (!isCapabilityAssetRuntimePath(ref.file_path)) return [];
    return [{
      source_media_asset_id: metadata.source_media_asset_id,
      asset_manifest_id: metadata.asset_manifest_id,
      runtime_path: ref.file_path,
      source_path: ref.file_name,
      mime_type: ref.file_mime_type,
      byte_size: ref.file_size_bytes,
      asset_type: metadata.asset_type,
      content_hash: metadata.content_hash,
      project_id: metadata.project_id,
      upload_batch_id: metadata.upload_batch_id,
    }];
  });
  if (!assets.length) return '';
  return `[BlueBear capability assets: ${JSON.stringify({
    schema_version: 'bluebear.chat_capability_asset_context.v1',
    assets,
  })}]`;
}


export function readCapabilityAssetFileBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
    reader.onerror = () => reject(reader.error || new Error('Failed to read asset file'));
    reader.readAsDataURL(file);
  });
}

export async function uploadChatAttachmentsToWorkspace({
  workspaceId,
  entries,
  saveDocs,
  handoffAssetFiles,
  projectId,
  uploadBatchId,
  readFileBase64 = readCapabilityAssetFileBase64,
}) {
  if (!workspaceId) throw new Error('workspaceId is required');
  const { assets, documents } = partitionChatAttachmentItems(entries);
  if (documents.length && typeof saveDocs !== 'function') throw new Error('saveDocs is required');
  if (assets.length && typeof handoffAssetFiles !== 'function') throw new Error('handoffAssetFiles is required');

  const documentUpload = documents.length
    ? Promise.all(documents.map(async (entry) => {
      const file = entry.file || entry;
      return {
        path: entry.path || file.webkitRelativePath || file.name,
        content: await readFileBase64(file),
        encoding: 'base64',
      };
    })).then((files) => saveDocs(workspaceId, { files }))
    : Promise.resolve(null);
  const assetUpload = assets.length
    ? handoffAssetFiles(workspaceId, assets, {
      projectId: projectId || workspaceId,
      ...(uploadBatchId ? { uploadBatchId } : {}),
      readFileBase64,
    })
    : Promise.resolve(null);

  const [documentResult, assetResult] = await Promise.all([documentUpload, assetUpload]);
  return {
    documentCount: documents.length,
    assetCount: assets.length,
    documentResult,
    assetResult,
    assetFileRefs: capabilityAssetFileRefsFromHandoff(assetResult),
  };
}

export async function uploadCapabilityAssetsToWorkspace({
  workspaceId,
  files,
  projectId,
  uploadBatchId,
  destination,
  handoffAssets,
  readFileBase64 = readCapabilityAssetFileBase64,
  validateOptions,
}) {
  if (!workspaceId) throw new Error('workspaceId is required');
  if (typeof handoffAssets !== 'function') throw new Error('handoffAssets is required');

  const items = normalizeCapabilityAssetUploadItems(files);
  const validationError = validateCapabilityAssetUploadItems(items, validateOptions);
  if (validationError) throw new Error(validationError);

  const payload = await buildCapabilityAssetHandoffPayload(items, {
    projectId: projectId || workspaceId,
    uploadBatchId,
    destination,
    readFileBase64,
  });

  return handoffAssets(workspaceId, payload);
}
